import { type FormEvent, useCallback, useEffect, useId, useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { isClerkAPIResponseError } from '@clerk/clerk-react/errors'

const SAVE_FAILED = "Couldn't save your name. Try again."

/** Clerk's own message for a failed save, else the error's message, else a plain one. */
function saveErrorMessage(err: unknown): string {
  if (err && isClerkAPIResponseError(err)) {
    const clerkMessage = err.errors[0]?.longMessage || err.errors[0]?.message
    if (clerkMessage) return clerkMessage
  }
  if (err instanceof Error && err.message) return err.message
  return SAVE_FAILED
}

interface ProfileModalProps {
  open: boolean
  onClose: () => void
  /** Called after Clerk has saved the name (the modal has already called onClose). */
  onSaved: () => void
}

/** Header → Edit: change your first and last name, saved in Clerk (so the
 *  header shows the new name straight away). The form only exists while the
 *  modal is open, so every open starts from Clerk's current user, with no
 *  leftover error. (The AI layout picker left in 2026-10: the AI is one side
 *  panel now, opened from the menu.) */
export default function ProfileModal({ open, ...props }: ProfileModalProps) {
  return open ? <ProfileDialog {...props} /> : null
}

function ProfileDialog({ onClose, onSaved }: Omit<ProfileModalProps, 'open'>) {
  const { user } = useUser()
  const id = useId()
  const titleId = `${id}-title`
  const firstNameId = `${id}-first-name`
  const lastNameId = `${id}-last-name`
  const emailId = `${id}-email`
  const [firstName, setFirstName] = useState(user?.firstName ?? '')
  const [lastName, setLastName] = useState(user?.lastName ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Escape, the overlay and Cancel close the modal, but not while a save is running.
  const close = useCallback(() => {
    if (!saving) onClose()
  }, [saving, onClose])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [close])

  const save = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (saving) return
    if (!user) {
      setError(SAVE_FAILED)
      return
    }
    setSaving(true)
    setError(null)
    try {
      await user.update({ firstName: firstName.trim(), lastName: lastName.trim() })
    } catch (err) {
      setError(saveErrorMessage(err))
      setSaving(false)
      return
    }
    setSaving(false)
    onClose()
    onSaved()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        className="fixed inset-0 cursor-default"
        onClick={close}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white dark:bg-[#0c0c0f] border border-slate-200 dark:border-zinc-800/80 w-full max-w-md rounded-2xl shadow-xl p-6 relative z-10"
      >
        <h3 id={titleId} className="font-display font-bold text-lg text-slate-900 dark:text-white tracking-tight mb-1">
          Change User Data
        </h3>
        <p className="text-xs text-slate-400 dark:text-zinc-500 mb-5">
          Update your personal profile. Email is managed by identity provider.
        </p>

        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5 text-left">
              <label htmlFor={firstNameId} className="text-[10px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wider">
                First Name
              </label>
              <input
                id={firstNameId}
                type="text"
                autoFocus
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-zinc-900/50 text-slate-900 dark:text-white border border-slate-200 dark:border-zinc-800 rounded-xl focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent"
              />
            </div>
            <div className="space-y-1.5 text-left">
              <label htmlFor={lastNameId} className="text-[10px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wider">
                Last Name
              </label>
              <input
                id={lastNameId}
                type="text"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                className="w-full px-3.5 py-2 text-sm bg-slate-50 dark:bg-zinc-900/50 text-slate-900 dark:text-white border border-slate-200 dark:border-zinc-800 rounded-xl focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent"
              />
            </div>
          </div>

          <div className="space-y-1.5 text-left">
            <label htmlFor={emailId} className="text-[10px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wider">
              Email (Read-only)
            </label>
            <input
              id={emailId}
              type="email"
              disabled
              value={user?.primaryEmailAddress?.emailAddress || ''}
              className="w-full px-3.5 py-2 text-sm bg-slate-100 dark:bg-zinc-900/20 border border-slate-200 dark:border-zinc-800 rounded-xl text-slate-400 dark:text-zinc-600 cursor-not-allowed"
            />
          </div>

          {error ? (
            <p role="alert" className="text-xs text-rose-600 dark:text-rose-400">
              {error}
            </p>
          ) : null}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={close}
              disabled={saving}
              className="px-4 py-2 text-xs font-semibold hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-700 dark:text-zinc-300 rounded-xl transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-xs font-semibold bg-accent text-white hover:bg-accent/90 rounded-xl shadow-md transition-all flex items-center gap-1.5"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
